import { z } from 'zod'

import { type SdkLanguage, sdkPreviewSchema } from '@scalar-org/entities/sdk'
import { nanoidSchema } from '@scalar-org/entities/utility'

import type { RequestService } from '../request'

export function sdksApiFactory(requestService: RequestService) {
  async function create(data: { apiUid: string; language: SdkLanguage }) {
    return await requestService.request({
      url: 'core/v1/sdks',
      method: 'post',
      data,
      schema: z.string(),
    })
  }

  async function bulkCreate(data: {
    apiUid: string
    languages: SdkLanguage[]
    className?: string
  }) {
    return await requestService.request({
      url: 'core/v1/sdks/bulk',
      method: 'post',
      data,
      schema: z.object({
        uids: nanoidSchema.array(),
      }),
    })
  }

  async function update(
    uid: string,
    data: {
      title?: string
      config?: string
      namespace?: string
      isPrivate?: boolean
      slug?: string
    },
  ) {
    return await requestService.request({
      url: `core/v1/sdks/${uid}`,
      method: 'patch',
      data,
      schema: z.null(),
    })
  }

  async function del(uid: string) {
    return await requestService.request({
      url: `core/v1/sdks/${uid}`,
      method: 'delete',
      schema: z.null(),
    })
  }

  async function available(data: { namespace: string; slug: string }) {
    return await requestService.request({
      url: 'core/v1/sdks/available',
      method: 'post',
      data,
      schema: z.object({
        available: z.boolean(),
      }),
    })
  }

  async function addAccessGroup(
    uid: string,
    data: {
      groupUid: string
    },
  ) {
    return await requestService.request({
      url: `core/v1/sdks/${uid}/access-group`,
      method: 'post',
      data,
      schema: z.null(),
    })
  }

  async function removeAccessGroup(
    uid: string,
    data: {
      groupUid: string
    },
  ) {
    return await requestService.request({
      url: `core/v1/sdks/${uid}/access-group`,
      method: 'delete',
      data,
      schema: z.null(),
    })
  }

  async function linkRepo(data: {
    uid: string
    repositoryId: number
    baseBranch: string
  }) {
    return await requestService.request({
      url: 'core/v1/sdks/repository',
      method: 'post',
      data,
      schema: z.null(),
    })
  }

  async function updateRepo(data: { uid: string; baseBranch: string }) {
    return await requestService.request({
      url: 'core/v1/sdks/repository/update',
      method: 'post',
      data,
      schema: z.null(),
    })
  }

  async function unlinkRepo(uid: string) {
    return await requestService.request({
      url: 'core/v1/sdks/repository',
      method: 'delete',
      data: { uid },
      schema: z.null(),
    })
  }

  async function syncBuild(buildUid: string) {
    return await requestService.request({
      url: 'core/v1/sdks/sync',
      method: 'post',
      data: { buildUid },
      schema: z.null(),
    })
  }

  async function build(sdkUid: string, version: string) {
    return await requestService.request({
      url: 'core/v1/sdks/build',
      method: 'post',
      data: { sdkUid, version },
      schema: z.null(),
    })
  }

  async function generateSnippets(data: {
    namespace: string
    slug: string
    languages: SdkLanguage[]
    className?: string
  }) {
    return await requestService.request({
      url: 'core/v1/sdks/snippets',
      method: 'post',
      data,
      schema: sdkPreviewSchema.array(),
    })
  }

  return {
    create,
    bulkCreate,
    update,
    delete: del,
    available,
    addAccessGroup,
    removeAccessGroup,
    linkRepo,
    unlinkRepo,
    updateRepo,
    build,
    syncBuild,
    generateSnippets,
  }
}
